import React, { useMemo, useState } from 'react';

import { createSearchParams } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';

import SearchBigQueryDateRangePicker, {
  getDefaultSearchDateRange,
  toBigQueryExclusiveEndDate,
  toInclusiveEndDate,
} from '~/components/Content/SearchAnalytics/SearchBigQueryDateRangePicker';

import { useGetAnalyticsSearchStats } from '~/hooks/apis/analytics/useGetAnalyticsSearchStats';

import type {
  AnalyticsDateRange,
  AnalyticsSearchCategoryResult,
  AnalyticsSearchStatsParams,
} from '~/services/AnalyticsService';
import type { Dayjs } from '~/services/TimeService';

import { formatAnalyticsCategoryLabel, splitExactMatchSearches } from './splitExactMatchSearches';

const DEFAULT_SEARCH_STATS_LIMIT = 100;
const MIN_SEARCH_STATS_LIMIT = 1;
const MAX_SEARCH_STATS_LIMIT = 1000;

function buildReproduceSearchHref(searchId: string, dateRange: AnalyticsDateRange) {
  const inclusiveEndDate = toInclusiveEndDate(dateRange.endDate);

  return `/search/reproduce-search?${createSearchParams({
    searchId,
    startDate: dateRange.startDate,
    endDate: inclusiveEndDate,
  }).toString()}`;
}

function renderSampleSearchLink(searchId: string, dateRange: AnalyticsDateRange | undefined) {
  if (!dateRange) {
    return searchId;
  }

  return (
    <Link href={buildReproduceSearchHref(searchId, dateRange)} target="_blank" rel="noopener noreferrer">
      View sample
    </Link>
  );
}

type AnalyticsCategoriesTableProps = {
  categories: Array<AnalyticsSearchCategoryResult>;
  dateRange: AnalyticsDateRange | undefined;
};

function AnalyticsCategoriesTable({ categories, dateRange }: AnalyticsCategoriesTableProps) {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const handleToggleCategory = (searchCategory: string) => {
    setExpandedCategories((currentExpandedCategories) => ({
      ...currentExpandedCategories,
      [searchCategory]: !currentExpandedCategories[searchCategory],
    }));
  };

  return (
    <TableContainer>
      <Table size="small" sx={{ '& th, & td': { verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>#</TableCell>
            <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>Category</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              Searches per category to load
            </TableCell>
            <TableCell>Top sample searches</TableCell>
            <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>Sample search</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((result, categoryIndex) => {
            const isExpanded = Boolean(expandedCategories[result.searchCategory]);
            const hasExpandableSearches = result.topSearches.length > 1;
            const displayedTopSearches = isExpanded ? result.topSearches : result.topSearches.slice(0, 1);
            const rowSpan = Math.max(displayedTopSearches.length, 1);
            const categoryCells = (
              <>
                <TableCell rowSpan={rowSpan} sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                  {categoryIndex + 1}
                </TableCell>
                <TableCell rowSpan={rowSpan} component="th" scope="row" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">{formatAnalyticsCategoryLabel(result.searchCategory)}</Typography>
                </TableCell>
                <TableCell rowSpan={rowSpan} align="right">
                  {result.searchCount.toLocaleString()}
                </TableCell>
              </>
            );

            if (displayedTopSearches.length === 0) {
              return (
                <TableRow key={result.searchCategory}>
                  {categoryCells}
                  <TableCell colSpan={2}>
                    <Typography variant="body2" color="text.secondary">
                      No top searches
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            }

            return displayedTopSearches.map((search, searchIndex) => (
              <TableRow key={`${result.searchCategory}-${search.rank}`}>
                {searchIndex === 0 && categoryCells}
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    {searchIndex === 0 && hasExpandableSearches ? (
                      <IconButton
                        size="small"
                        aria-label={`${
                          isExpanded ? 'Collapse' : 'Expand'
                        } top searches for ${formatAnalyticsCategoryLabel(result.searchCategory)}`}
                        onClick={() => handleToggleCategory(result.searchCategory)}
                        sx={{ p: 0 }}
                      >
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 20, flexShrink: 0 }} />
                    )}
                    <Typography variant="body2">
                      {search.queryCount.toLocaleString()}x {search.naturalLanguageQuery}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                  {renderSampleSearchLink(search.searchId, dateRange)}
                </TableCell>
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type AnalyticsCategorySectionProps = {
  title: string;
  count: number;
  categories: Array<AnalyticsSearchCategoryResult>;
  dateRange: AnalyticsDateRange | undefined;
};

function AnalyticsCategorySection({ title, count, categories, dateRange }: AnalyticsCategorySectionProps) {
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title} ({count.toLocaleString()})
        </Typography>
        <AnalyticsCategoriesTable categories={categories} dateRange={dateRange} />
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getDefaultSearchDateRange);
  const [limit, setLimit] = useState(DEFAULT_SEARCH_STATS_LIMIT);
  const [submittedParams, setSubmittedParams] = useState<AnalyticsSearchStatsParams | null>(null);

  const { data, isFetching, error } = useGetAnalyticsSearchStats(submittedParams);
  const categories = data?.categories ?? [];

  const { mainCategories, exactMatchCategories, nlsSuggestionCategory } = useMemo(
    () => splitExactMatchSearches(categories),
    [categories],
  );
  const dateRangeFromResponse = data?.dateRange;
  const mainSearchCount = mainCategories.reduce((sum, category) => sum + category.searchCount, 0);
  const exactMatchSearchCount = exactMatchCategories.reduce((sum, category) => sum + category.searchCount, 0);
  const nlsSuggestionSearchCount = nlsSuggestionCategory?.searchCount ?? 0;
  const isLoadingResults = isFetching;
  const showResults = Boolean(data) && !isLoadingResults;
  const sectionResetKey = submittedParams
    ? `${submittedParams.startDate}-${submittedParams.endDate}-${submittedParams.limit}`
    : '';

  const [startDate, endDate] = dateRange;
  const isLimitValid = Number.isInteger(limit) && limit >= MIN_SEARCH_STATS_LIMIT && limit <= MAX_SEARCH_STATS_LIMIT;
  const isInputComplete = Boolean(startDate && endDate && isLimitValid);

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextLimit = event.target.valueAsNumber;
    setLimit(Number.isNaN(nextLimit) ? MIN_SEARCH_STATS_LIMIT : nextLimit);
  };

  const handleRunQuery = () => {
    if (!startDate || !endDate || !isLimitValid) {
      return;
    }

    setSubmittedParams({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: toBigQueryExclusiveEndDate(endDate),
      limit,
    });
  };

  return (
    <Stack spacing={0}>
      <SearchBigQueryDateRangePicker dateRange={dateRange} onChange={setDateRange}>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' },
          }}
        >
          <TextField
            label="Number of searches"
            type="number"
            value={limit}
            onChange={handleLimitChange}
            size="small"
            required
            error={!isLimitValid}
            helperText={
              !isLimitValid
                ? `Enter a value between ${MIN_SEARCH_STATS_LIMIT} and ${MAX_SEARCH_STATS_LIMIT}`
                : undefined
            }
            inputProps={{ min: MIN_SEARCH_STATS_LIMIT, max: MAX_SEARCH_STATS_LIMIT, step: 1 }}
            sx={{ width: { md: 180 } }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRunQuery}
              disabled={isLoadingResults || !isInputComplete}
            >
              Run query
            </Button>
          </Stack>
        </Box>
      </SearchBigQueryDateRangePicker>

      {(isLoadingResults || error) && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            {isLoadingResults && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  gap: 2,
                }}
              >
                <CircularProgress size={32} />
                <Typography variant="body2">Fetching stats from BigQuery</Typography>
              </Box>
            )}

            {!isLoadingResults && error && (
              <Alert severity="error">{error instanceof Error ? error.message : 'Failed to fetch analytics'}</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {showResults && (
        <AnalyticsCategorySection
          key={`main-${sectionResetKey}`}
          title="Potential NLS"
          count={mainSearchCount}
          categories={mainCategories}
          dateRange={dateRangeFromResponse}
        />
      )}

      {showResults && nlsSuggestionCategory && (
        <AnalyticsCategorySection
          key={`nls-${sectionResetKey}`}
          title="NLS suggestions"
          count={nlsSuggestionSearchCount}
          categories={[nlsSuggestionCategory]}
          dateRange={dateRangeFromResponse}
        />
      )}

      {showResults && exactMatchCategories.length > 0 && (
        <AnalyticsCategorySection
          key={`exact-${sectionResetKey}`}
          title="Standard searches"
          count={exactMatchSearchCount}
          categories={exactMatchCategories}
          dateRange={dateRangeFromResponse}
        />
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle>How to read these numbers</AlertTitle>
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
          <Typography component="li" variant="body2">
            <strong>Included:</strong> Searches submitted through the search bar during the selected dates. Empty
            queries are excluded; capitalisation and repeated spaces are ignored.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Source categories:</strong> Searches are grouped by query length—1–3, 4–6, or 7+ words—and whether
            typeahead matched an airport, landmark, destination, or hotel.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Counts:</strong> A category total counts all searches, not unique queries. “10x Bali” means that
            query was searched 10 times. The limit loads that many top unique queries <strong>per category</strong>.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Final sections:</strong> Known generated suggestions move to <strong>NLS suggestions</strong>.
            Queries matching a destination, landmark, or property move to <strong>Standard searches</strong>; close
            matches require at least 74% name coverage. Everything else stays in <strong>Potential NLS</strong>.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Important:</strong> Only the top queries loaded for each category can be reclassified. Increasing
            the limit may therefore change the three section totals. When a query moves, all of its counted searches
            move with it.
          </Typography>
        </Box>
      </Alert>
    </Stack>
  );
}
